import { CSSProperties, PropsWithChildren, useEffect, useRef, useState } from 'react'

import { styled } from '../styles'

export const ResponsiveOverflowSwitcherContainer = styled('div')({
  overflow: 'hidden',
  position: 'relative',
})

const Measure = styled('div')({
  pointerEvents: 'none',
  position: 'absolute',
  visibility: 'hidden',
})

const Wrapper = styled('div')({
  flex: 1,
  overflow: 'hidden',
})

interface ResponsiveOverflowSwitcherProps {
  extraCondition?: boolean
  extraStyles?: CSSProperties
  renderOverflowing: () => React.ReactNode
}

export const ResponsiveOverflowSwitcher: React.FC<PropsWithChildren<ResponsiveOverflowSwitcherProps>> = ({
  children,
  extraCondition = false,
  renderOverflowing,
}) => {
  const [isOverflowing, setIsOverflowing] = useState(false)

  const measureRef = useRef<HTMLDivElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const measureEl = measureRef.current
    const wrapperEl = wrapperRef.current

    if (!measureEl || !wrapperEl || extraCondition) return

    const checkOverflow = () => {
      const hasOverflow = measureEl.scrollWidth > wrapperEl.clientWidth
      setIsOverflowing(hasOverflow)
    }

    checkOverflow()

    const resizeObserver = new ResizeObserver(() => {
      checkOverflow()
    })

    resizeObserver.observe(wrapperEl)

    return () => {
      resizeObserver.disconnect()
    }
  }, [extraCondition])

  return (
    <>
      <Measure ref={measureRef}>{children}</Measure>
      <Wrapper ref={wrapperRef}>{isOverflowing ? renderOverflowing() : children}</Wrapper>
    </>
  )
}
